import {TEntity} from 'modules/data';
import {IDefaultTableStateFlat, TLookupValue} from 'modules/data/data-types';
import {TOracleBoolType} from 'shared/types';
import {ILookupEntry} from 'shared/types/lookups';

export enum EDocumentsSigningMethod {
    'UPLOAD_EDS_FILE' = 'UPLOAD_EDS_FILE',
    'EXTERNAL_SERVICE' = 'EXTERNAL_SERVICE',
    'CRYPTO_PRO_PLUGIN' = 'CRYPTO_PRO_PLUGIN'
}

export interface IDocumentsState {
    documentTypes?: IDocumentTypeResponse[];
    documentTypesOnUpload?: IDocumentTypeResponse[];
    branchNames?: IDocumentTypeResponse[];
    documentList?: TLookupValue[] | IDocumentTypeResponse[];
    fileUpload: IFileUploadState;
    signatureUpload: ISignatureUploadState;
    signatureExtensions?: ISignExtensionsState;
    totalStage?: {
        fileList?: IDocumentUploadFile[];
    };
    documentsUploadMonitoring?: IDocumentsUploadMonitoringState;
    pagination?: IDefaultTableStateFlat;
    signingMethod?: EDocumentsSigningMethod;
    documentsForUploadAttach?: {[sublistCode: string]: ILookupEntry[]};
    documentProperties?: IDocumentProperty;
    hasBranchesLinkingFlag: boolean;
}

export interface IDocumentsUploadMonitoringState {
    fileList?: IDocumentsMonitoringItem[];
    headers?: string;
}

export interface IDocumentProperty {
    [key: string]: boolean;
}

export interface IDocumentsMonitoringItem {
    fileName: string;
    saveResult: IDocumentsUploadMonitoring;
}

export interface IResetRowStateParams {
    tableRow: Record<string, any>;
    uniqueFieldName?: string;
    entityName?: string;
}

export enum EXsdValidationStatusCodes {
    NOT_APPLICABLE = 'NOT_APPLICABLE',
    SUCCESS = 'SUCCESS',
    UNSUCCESS = 'UNSUCCESS',
    ERROR_SAX = 'ERROR_SAX',
    ERROR_IO = 'ERROR_IO',
    ERROR_PARSER = 'ERROR_PARSER',
}

export interface IDocumentUploadFile extends TEntity {
    title: string;
    type?: string | number;
    sectionCode?: string;
    subsectionCode?: string;
    typeCode?: string;
    sectionId?: string | number;
    subsectionId?: string | number;
    xmlValidateStatusCode?: string;
    fileName: string;
    fileSize: string;
    uploadDate: string | null;
    correctionNumber?: number;
    regNum?: number;
    branchId?: string;
    templateCode?: string;
    xsdValidationStatus?: string;
    xsdValidationStatusLog?: string;
    xsdValidationStatusCode?: EXsdValidationStatusCodes;
    file: File;
    templatesTypeOptions?: IDocumentTemplatesTypeResponse[];
    [index: symbol]: any;
    fileUploadInterfaceId?: string;
}

export interface IDocumentUploadScan extends IDocumentUploadFile {
    documentFileId?: number;
    progress?: {
        percent: number;
        status?: EFileUploadStatus;
    };
    isSubmitted?: boolean;
}

export interface IDocumentSaveInfoFile {
    attachmentId: number;
    defaultAttachInfo: {
        fileName: string;
        branchName: null;
        branchId: null;
        outFileSize: string;
        docTypeCode: string;
        docTypeMean: string;
        docSectionCode: string;
        docSubsectionCode: string;
        extensions: EExtensionName;
        docSubsectionList: [
            {
                id: number;
                lookupType: string;
                lookupCode: string;
                meaning: string;
                description: null;
                parentLookupValueId: number;
                hibernateLazyInitializer: {};
            }
        ];
        validStatusCode: null;
        validStatusMean: null;
        useTimestampFlag: string;
        useCorrNumFlag: string;
        documentNamingRule: null;
    };
    stages: IStages[];
    autoStart: boolean;
}

export interface IStages {
    id: number;
    displaySequence: number;
    name: string;
    monitorProgramSetId: number;
    monitorProgramId: number;
    frontBackUrl: string;
}

export interface IDocumentUploadSignature extends TEntity {
    createdAt: Date;
    fileName: string;
    documentFileId?: string;
    documentName?: string;
    documentTypeName?: string;
    sectionCode?: string;
    subsectionCode?: string;
    file: File;
    attachId?: number;
    docTypeId?: number;
    corrNum?: string;
    branchId?: number;
}

type TDocumentType = Omit<ILookupEntry, 'id'> & {
    id: string;
}

export interface IDocumentTemplatesTypeResponse extends TDocumentType {}

export interface IDocumentTypeResponse extends TDocumentType {
    description: string;
}

export interface ISignExtensionsState {
    accept?: string;
}

export interface IDocumentUploadedSignature extends TEntity {
}

export interface IDocumentFileUploadForm {
    uploadFiles: IDocumentUploadFileRequest[];
}

export interface IDocumentUploadFileRequest {
    documentName: string;
    docTypeCode?: string;
    sectionCode?: string | number;
    subSectionCode?: string | number;
    docTypeId?: string | number;
    sectionId?: string | number;
    subSectionId?: string | number;
    xmlValidateStatusCode?: string;
    branchId?: string;
    startDocDate?: string;
    corrNum?: number;
    file?: File;
    fileUploadInterfaceId?: string;
    shouldUnzip?: boolean;

    [key: string]: string | Blob | undefined | number | null | boolean;
}

export interface IDocumentUploadSignatureRequest {
    attachId?: string;
    file: File;

    [key: string]: string | Blob | undefined | number;
}

export interface IDocumentUploadFileResponse {
    fileName: string;
    outFileSize: string;
    docTypeCode: string;
    docTypeMean: string;
    docSectionCode: string;
    docSubsectionCode: string;
    docTypeId?: string;
    sectionId?: string;
    subsectionId?: string;
}

export interface IFileAttachInfoResponse {
    filename: string;
    defaultName: string;
    defaultSection: string;
    defaultSubSection: string;
    defaultBranchId: string;
    defaultDocTypeId: number;
}

export interface IDocumentUploadSignatureResponse {
    attachmentId: string;
    docFileName: string;
    docId: string;
    docName: string;
    docSection: string;
    docType: string;
    signatureFileContent: string;
    signatureFileName: string;
    status: string;
}

export interface IDocumentFileInfo {
    attachmentId: string;
    docFileName: string;
    docId: string;
    docName: string;
    docSection: string;
    docType: string;
    signatureFileContent: string;
    signatureFileName: string;
    status: string;
}

export interface IFileUploadState {
    [key: string]: IEntityFileUploadState;
}

export interface IEntityFileUploadState {
    activityList: Record<string, IFileUploadType>;
    fileList: IDocumentUploadFile[];
    sessionId?: string;
}

export interface IFileUploadType {
    uploadStatus?: EFileUploadStatus;
    uploadPercent?: number;
    parsingStatus?: EFileParsingStatus;
    parsingPercent?: number;
    validated?: boolean;
    validatedMeaning?: string;
    editFieldsPermission?: {
        [key: string]: {
            activated?: boolean;
            required?: boolean;
        };
    };
}

export interface ISignUploadType {
    uploadStatus?: EFileUploadStatus;
    uploadPercent?: number;
}

export interface ISignatureUploadState {
    [key: string]: IEntitySignatureUploadState;
}

export interface IEntitySignatureUploadState {
    activityList: Record<string, ISignatureUploadType>;
    signList: IDocumentUploadSignature[];
}

export interface ISignatureUploadType {
    uploadStatus?: EFileUploadStatus;
    uploadPercent?: number;
    parsingStatus?: EFileParsingStatus;
    parsingPercent?: number;
    validated?: boolean;
    validatedMeaning?: string;
    editFieldsPermission?: {
        [key: string]: TOracleBoolType;
    };
}

export interface IFileActivitySetPayload {
    entityName: string;
    uniqueValue: string | number;
    item: IFileUploadType;
}

export interface ISessionIdSetPayload {
    entityName: string;
    sessionId: string;
}

export interface IFileActivityResetItemPayload {
    entityName: string;
    uniqueValue: string | number;
}

export interface IFileActivityResetAllPayload {
    entityName: string;
}

export interface IFileListSetPayload {
    entityName: string;
    fileList: IDocumentUploadFile[];
}

export interface IFileListResetPayload {
    entityName: string;
}

export interface IFileListDtoPayload {
    entityName: string;
    fileList: IDocumentUploadFile[];
}

export interface ISignActivitySetPayload {
    entityName: string;
    uniqueValue: string | number;
    item: ISignUploadType;
}

export interface ISignActivityResetPayload {
    entityName: string;
}

export interface ISignListSetPayload {
    entityName: string;
    signList: IDocumentUploadSignature[];
}

export interface ISignListResetPayload {
    entityName: string;
}

export interface ISingSetExtensionsPayload {
    accept?: string;
}

export interface ISetDocumentsForUploadAttachPayload {
    documents: ILookupEntry[];
    sublistCode: string;
}

export interface ISetDocumentScansListPayload {
    scans: IDocumentUploadScan[];
    entityName: string;
}

export enum EFileUploadStatus {
    NORMAL = 'normal',
    ACTIVE = 'active',
    EXCEPTION = 'exception',
    SUCCESS = 'success',
}

export enum EFileParsingStatus {
    NORMAL = 'normal',
    ACTIVE = 'active',
    EXCEPTION = 'exception',
    SUCCESS = 'success',
}

export enum EFileValidationStatus {
    VALID = 'valid',
    INVALID = 'invalid',
}

export type TFileRowStateDataType = {
    /**
     * timestamp-enabled & corrnum-enabled
     */
    docTypeIdC?: string;
    /**
     * validate-status
     */
    docFile?: Blob;
    docTypeCode?: string;
};

export enum EFileSizeFormat {
    B = 'B',
    KB = 'KB',
    MB = 'MB',
    GB = 'GB',
    FLEX = 'FLEX',
}

// --- documents-upload-monitoring-types ---
export interface IExecuteMonitoringData {
    // eslint-disable-next-line camelcase
    org_id: string;
    // eslint-disable-next-line camelcase
    template_code: string;
     // eslint-disable-next-line camelcase
    table_report_page_request_dto: any;
}

export interface IMonitoringProgram {
    id: number;
    code: string;
    name: string;
    description: string;
}

export interface IProgramSetProgram {
    id: number;
    program: IMonitoringProgram;
}

export interface IProgramSetStage {
    id: number;
    displaySequence: number;
    code: string;
    name: string;
    description: string;
    programSetPrograms: IProgramSetProgram[];
}

export interface IMonitorProgramSet {
    id: number;
    code: string;
    name: string;
    description: string;
    programSetStages: IProgramSetStage[];
}

export interface IDocumentsUploadMonitoring {
    parentRequestId: number; // монитор который следит за выполнением всего набора программ
    monitorProgramSet: IMonitorProgramSet; // информация о наборе программ
}

export interface IMonitoringResultLog {
    id: number;
    monitorProgramRequest: number;
    description: string;
    nameFile: string;
}

export interface IMonitoringResult {
    id: number;
    programId: number;
    phase: TLookupValue<EPhaseStatus>;
    status: TLookupValue<EMonitoringStatusCode>;
    monitorProgramLog?: IMonitoringResultLog;
}

export interface IDocumentsUploadMonitoringResult { // результат мониторинга программы
    id: number;
    parentRequest: IMonitoringResult; // информация о статусе и фазе всех стадий
    program: IMonitoringProgram; // текущая программа которая выполняется
    request: IMonitoringResult; // информация о статусе и программы
}
export enum EExtensionName {
    docxIcon = 'docx',
    xmlIcon = 'xml',
    jsonIcon = 'json',
    docIcon = 'doc',
    xlsxIcon = 'xlsx',
    fileIcon = 'file'
}

export enum EStageStatus {
    success = 'success',
    reject = 'reject',
    initial = 'initial',
    pending = 'pending'
}

export enum EPhaseStatus {
    completed = 'C',
    inactive = 'I',
    pending = 'P',
    running = 'R',
    warning = 'W',
    cancelled = 'D',
    error = 'E',
    onHold = 'G',
}

export enum EMonitoringStatusCode {
    normal = 'C',
    cancelled = 'D',
    error = 'E',
    onHold = 'G',
    running = 'R',
    warning = 'W'
}

export interface IValidationResult {
    id: number;
    lookupType: string;
    statusCode: EXsdValidationStatusCodes;
    meaning: string;
    description: string;
    fileName: string;
    docTypeCode: string;
}

export interface IFileValidationResponse {
    strictMode: boolean;
    validationResult: IValidationResult;
}
